import * as React from "react";
import { cn } from "@/lib/utils";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Visible label text (required for accessibility). */
  label: string;
  /** Optional hint text displayed below the label. */
  hint?: string;
  /** Error message. When set the input enters its error state and the
   *  message is linked via `aria-describedby`. */
  error?: string;
}

/**
 * Styled text input with a visible `<label>`, optional hint, and an error
 * state wired up via `aria-describedby` for screen-reader support.
 *
 * Meets the 44 px minimum touch-target requirement on mobile.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, className, id: idProp, type = "text", ...props }, ref) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;

    const describedBy = [hint ? hintId : null, error ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {/* Label */}
        <label
          htmlFor={id}
          className="text-sm font-semibold text-dark"
        >
          {label}
        </label>

        {/* Hint */}
        {hint && (
          <p id={hintId} className="text-xs text-mid">
            {hint}
          </p>
        )}

        {/* Input */}
        <input
          ref={ref}
          id={id}
          type={type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            // Base
            "min-h-[44px] w-full rounded-md border bg-paper px-4 py-2.5",
            "text-base text-dark placeholder:text-mid/70",
            "transition-colors duration-150",
            // Focus
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
            // Default / error border
            error
              ? "border-error focus-visible:outline-error"
              : "border-line hover:border-mid/50",
            // Disabled
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />

        {/* Error message */}
        {error && (
          <p id={errorId} role="alert" className="text-xs font-medium text-error">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
export type { InputProps };
